import { useState, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../utils/context";
import { PROJECT_NAME } from "../utils/constants";
import {
  Plus,
  FileText,
  MessageSquare,
  Trash2,
  LogOut,
  Search,
  LayoutDashboard,
  History,
  ChevronRight,
  Zap,
  Sparkles,
  Command
} from "lucide-react";

// API & Hooks
import {
  createResearch,
  deleteResearch
} from "../api/userApi";
import { useResearch } from "../hooks/useResearch";

// Modular Components
import CreateResearchModal from "../components/CreateResearchModal";
import Modal from "../components/Modal";

export default function MainPage() {
  const { user, logout } = useContext(UserContext);
  const navigate = useNavigate();

  // Custom Hook for Data
  const {
    researches,
    setResearches,
    quotas,
    isLoading
  } = useResearch(user?.uid);

  // UI State
  const [isCreateResearchModalOpen, setIsCreateResearchModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleCreateResearch = async (name) => {
    const tempId = Date.now();
    const newResearch = { id: tempId, title: name, items: [], createdAt: new Date().toISOString() };
    setResearches([newResearch, ...researches]);

    const response = await createResearch(user.uid, name);
    if (response.status) {
      setResearches(prev => prev.map(r => r.id === tempId ? { ...r, id: response.data.id, ...response.data } : r));
      navigate(`/research/${response.data.id}/dashboard`);
    } else {
      alert("Failed to create research.");
    }
  };

  const handleDeleteResearch = (e, research) => {
    e.stopPropagation();
    setDeleteTarget({ type: 'research', id: research.id, name: research.title });
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const response = await deleteResearch(user.uid, deleteTarget.id);
    if (response.status) {
      setResearches(prev => prev.filter(r => r.id !== deleteTarget.id));
    }
    setDeleteModalOpen(false);
    setDeleteTarget(null);
  };

  const filteredResearches = researches.filter(r =>
    r.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#0f172a] text-slate-100 selection:bg-indigo-500/30 overflow-x-hidden">
      {/* Background Glows */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-5%] left-[-5%] w-[45%] h-[45%] bg-indigo-500/10 blur-[100px] rounded-full animate-pulse"></div>
        <div className="absolute bottom-[-5%] right-[-5%] w-[45%] h-[45%] bg-blue-500/5 blur-[100px] rounded-full animate-pulse" style={{ animationDelay: '1.2s' }}></div>
      </div>

      {/* Modern Header */}
      <header className="relative z-20 px-8 py-5 border-b border-slate-800 bg-[#0f172a]/90 backdrop-blur-2xl sticky top-0 flex justify-between items-center transition-all duration-300">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-indigo-500/20">
            <Command size={20} strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="text-lg font-bold tracking-tight flex items-center gap-2">
              {PROJECT_NAME} <span className="text-slate-500 uppercase text-[9px] tracking-[0.2em] font-black ml-2 px-2 py-0.5 border border-slate-700/50 rounded-full">Vault</span>
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-6">
          <div className="hidden md:flex items-center gap-4 bg-slate-800/50 px-4 py-2 rounded-xl border border-slate-700 text-[9px] font-black tracking-widest uppercase">
            <div className="flex items-center gap-1.5 text-indigo-400">
              <FileText size={14} /> {quotas.report}
            </div>
            <div className="flex items-center gap-1.5 text-blue-400">
              <MessageSquare size={14} /> {quotas.chat}
            </div>
          </div>
          <div className="h-4 w-px bg-slate-800 mx-2 hidden sm:block"></div>
          <button
            onClick={handleLogout}
            className="p-2.5 rounded-xl bg-slate-800/50 border border-slate-700 hover:bg-slate-700 text-slate-400 hover:text-white transition-all active:scale-95"
          >
            <LogOut size={18} />
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-8 py-16">
        {/* Hero Section */}
        <div className="mb-20 space-y-6 text-center max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-[10px] font-black uppercase tracking-widest mb-2"
          >
            <Sparkles size={12} /> Knowledge Management
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-7xl font-black tracking-tight text-white leading-tight"
          >
            Research <span className="text-indigo-400">Vault</span>.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-slate-400 text-lg md:text-xl font-medium leading-relaxed"
          >
            Your secure repository for academic synthesis. <br className="hidden md:block" />
            Organize insights and collaborate with AI experts.
          </motion.p>
        </div>

        {/* Dashboard Controls */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-12 bg-slate-900/50 border border-slate-800 p-6 rounded-[2.5rem] backdrop-blur-xl shadow-2xl">
          <div className="relative w-full md:w-96 group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-indigo-400 transition-colors" size={18} />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-slate-800/50 border border-slate-700 hover:border-indigo-500/30 rounded-2xl pl-12 pr-6 py-4 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all w-full"
            />
          </div>

          <button
            onClick={() => setIsCreateResearchModalOpen(true)}
            className="flex items-center gap-3 bg-indigo-600 hover:bg-indigo-500 text-white px-10 py-4 rounded-2xl font-bold text-sm uppercase tracking-widest transition-all shadow-xl shadow-indigo-600/20 active:scale-95 group w-full md:w-auto"
          >
            <Plus size={20} className="group-hover:rotate-90 transition-transform duration-500" />
            New Project
          </button>
        </div>

        <section>
          <div className="flex items-center justify-between px-2 mb-8">
            <h3 className="text-xs font-black uppercase tracking-[0.2em] text-white/40 flex items-center gap-2">
              <History size={16} className="text-indigo-500" />
              Project History
            </h3>
            <span className="text-[10px] font-black text-white/20 uppercase tracking-widest">{filteredResearches.length} Projects</span>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="h-64 bg-white/5 rounded-[3rem] border border-white/5 animate-pulse"></div>
              ))}
            </div>
          ) : filteredResearches.length === 0 ? (
            <div className="py-32 text-center bg-white/[0.02] rounded-[4rem] border border-dashed border-white/10 flex flex-col items-center">
              <div className="w-20 h-20 bg-white/5 rounded-[2rem] flex items-center justify-center mb-8 text-white/10 group animate-float">
                <LayoutDashboard size={40} />
              </div>
              <h3 className="text-2xl font-black mb-3">No research found</h3>
              <p className="text-white/30 text-base max-w-sm font-medium">Your research intelligence vault is empty. Initialize your first investigation.</p>
              <button
                onClick={() => setIsCreateResearchModalOpen(true)}
                className="mt-10 text-indigo-400 font-black text-sm uppercase tracking-widest hover:text-white transition-colors"
              >
                + Create Research Project
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredResearches.map((research, index) => (
                <motion.div
                  key={research.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => navigate(`/research/${research.id}/dashboard`)}
                  className="group relative bg-[#131b2e] p-8 rounded-[3rem] border border-white/5 hover:border-indigo-500/40 transition-all cursor-pointer shadow-xl overflow-hidden flex flex-col h-72 group"
                >
                  {/* Visual Decor */}
                  <div className="absolute right-[-20%] top-[-20%] w-64 h-64 bg-indigo-500/10 blur-[100px] opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

                  <div className="flex justify-between items-start mb-8 relative z-10">
                    <div className="w-14 h-14 bg-white/[0.03] border border-white/5 text-indigo-400 rounded-2xl flex items-center justify-center group-hover:bg-indigo-500 group-hover:text-white transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 shadow-xl">
                      <LayoutDashboard size={28} />
                    </div>
                    <button
                      onClick={(e) => handleDeleteResearch(e, research)}
                      className="p-2.5 text-white/[0.05] hover:text-red-500 hover:bg-red-500/10 rounded-2xl transition-all relative z-20 group-hover:text-white/20"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>

                  <div className="flex-1 relative z-10">
                    <h3 className="text-2xl font-black mb-3 group-hover:text-indigo-400 transition-colors line-clamp-2 leading-tight tracking-tight">
                      {research.title}
                    </h3>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-black uppercase tracking-widest">
                        <Zap size={10} fill="currentColor" /> {research.items?.length || 0} Artifacts
                      </div>
                      <p className="text-[10px] text-white/20 font-black uppercase tracking-[0.1em]">
                        Modified {new Date(research.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  <div className="mt-8 flex items-center justify-between relative z-10">
                    <div className="h-px flex-1 bg-white/[0.03] mr-4"></div>
                    <div className="text-white/10 group-hover:text-indigo-400 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest transition-all transform translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100">
                      Enter Workspace
                      <ChevronRight size={14} />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </section>

        {/* Footer Stats */}
        <div className="mt-32 grid grid-cols-1 sm:grid-cols-3 gap-12 max-w-4xl mx-auto border-t border-white/5 pt-16">
          <div className="text-center group">
            <div className="text-4xl font-black mb-2 bg-clip-text text-transparent bg-gradient-to-b from-white to-white/40 group-hover:text-indigo-400 transition-colors tracking-tighter">99.9%</div>
            <div className="text-white/20 text-[10px] font-black uppercase tracking-[0.2em]">Neural Accuracy</div>
          </div>
          <div className="text-center group border-x border-white/5 px-8">
            <div className="text-4xl font-black mb-2 bg-clip-text text-transparent bg-gradient-to-b from-white to-white/40 group-hover:text-indigo-400 transition-colors tracking-tighter">{researches.length}</div>
            <div className="text-white/20 text-[10px] font-black uppercase tracking-[0.2em]">Vaulted Entities</div>
          </div>
          <div className="text-center group">
            <div className="text-4xl font-black mb-2 bg-clip-text text-transparent bg-gradient-to-b from-white to-white/40 group-hover:text-indigo-400 transition-colors tracking-tighter flex items-center justify-center gap-1">
              Low Latency
            </div>
            <div className="text-white/20 text-[10px] font-black uppercase tracking-[0.2em]">Compute Optimized</div>
          </div>
        </div>
      </main>

      <CreateResearchModal
        isOpen={isCreateResearchModalOpen}
        onClose={() => setIsCreateResearchModalOpen(false)}
        onCreate={handleCreateResearch}
      />

      <Modal isOpen={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} title="Destroy Vault" footer={
        <>
          <button onClick={() => setDeleteModalOpen(false)} className="flex-1 px-5 py-3 rounded-2xl bg-white/5 text-white/40 hover:text-white font-black uppercase text-xs tracking-widest transition-all">Abort</button>
          <button onClick={confirmDelete} className="flex-1 px-5 py-3 rounded-2xl bg-red-600/20 border border-red-500/30 text-red-100 font-black uppercase text-xs tracking-widest hover:bg-red-600 transition-all shadow-xl shadow-red-600/20">Confirm Deletion</button>
        </>
      }>
        <div className="flex flex-col items-center text-center py-6">
          <div className="w-20 h-20 bg-red-500/10 text-red-500 rounded-[2rem] flex items-center justify-center mb-8 ring-1 ring-red-500/20 shadow-2xl shadow-red-500/20 animate-pulse">
            <Trash2 size={32} />
          </div>
          <p className="text-xl font-bold text-white/90 leading-relaxed">Are you sure you want to permanently <br /> destroy <span className="text-red-400 font-black">"{deleteTarget?.name}"</span>?</p>
          <p className="mt-4 text-xs text-white/30 uppercase tracking-widest font-black">This action is irreversible</p>
        </div>
      </Modal>
    </div>
  );
}
